import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { money, date } from '../lib/format.js';
import { Modal } from '../components/Modal.jsx';

export default function Payments() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['payments'], queryFn: () => api('/api/payments') });
  const [creating, setCreating] = useState(false);
  const del = useMutation({ mutationFn: id => api(`/api/payments/${id}`, { method: 'DELETE' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['payments'] }) });
  return (
    <>
      <div className="page-header">
        <h1>Payments</h1>
        <button className="btn btn-primary" onClick={() => setCreating(true)}>New payment</button>
      </div>
      <table className="table">
        <thead>
          <tr><th>Date</th><th>Client</th><th>Method</th><th style={{ textAlign: 'right' }}>Amount</th><th>Ref</th><th>Allocated to</th><th></th></tr>
        </thead>
        <tbody>
          {data?.payments?.map(p => (
            <tr key={p.id}>
              <td>{date(p.receivedAt)}</td>
              <td>—</td>
              <td>{p.method}</td>
              <td style={{ textAlign: 'right' }}>{money(p.amount, p.currencyCode)}</td>
              <td>{p.reference || '—'}</td>
              <td>{p.allocations?.map(a => a.document.fullNumber).join(', ') || '—'}</td>
              <td><button className="btn btn-danger btn-sm" onClick={() => window.confirm('Delete payment? Documents will be re-balanced.') && del.mutate(p.id)}>×</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {creating && <PaymentModal onClose={() => setCreating(false)} onDone={() => { qc.invalidateQueries({ queryKey: ['payments'] }); setCreating(false); }} />}
    </>
  );
}

function PaymentModal({ onClose, onDone }) {
  const { data: clients } = useQuery({ queryKey: ['clients-all'], queryFn: () => api('/api/clients') });
  const [clientId, setClientId] = useState('');
  const [form, setForm] = useState({ method: 'BANK', amount: 0, currencyCode: 'EUR', exchangeRate: 1, receivedAt: new Date().toISOString().slice(0, 10), reference: '', note: '' });
  const [allocs, setAllocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const { data: docs } = useQuery({
    queryKey: ['open-docs', clientId],
    queryFn: () => api(`/api/documents?clientId=${clientId}&type=INVOICE`),
    enabled: !!clientId,
  });
  const open = docs?.documents?.filter(d => ['SENT', 'PARTIALLY_PAID', 'OVERDUE'].includes(d.status)) || [];

  const allocSum = allocs.reduce((s, a) => s + Number(a.amount || 0), 0);
  const canSubmit = clientId && form.amount > 0 && allocSum <= form.amount + 0.0001;

  const submit = async e => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      await api('/api/payments', { method: 'POST', body: {
        clientId, ...form, amount: Number(form.amount), exchangeRate: Number(form.exchangeRate),
        allocations: allocs.filter(a => Number(a.amount) > 0).map(a => ({ documentId: a.documentId, amount: Number(a.amount) })),
      }});
      onDone();
    } catch (e) { setErr(e.data?.error || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="New payment" onClose={onClose} wide>
      <form onSubmit={submit}>
        {err && <div className="error-banner">{err}</div>}
        <div className="row">
          <div className="field"><label>Client</label>
            <select required value={clientId} onChange={e => { setClientId(e.target.value); setAllocs([]); }}>
              <option value="">—</option>
              {clients?.clients?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Date</label><input type="date" value={form.receivedAt} onChange={e => setForm(f => ({ ...f, receivedAt: e.target.value }))} /></div>
          <div className="field"><label>Method</label>
            <select value={form.method} onChange={e => setForm(f => ({ ...f, method: e.target.value }))}>
              {['CASH', 'BANK', 'CARD', 'OTHER'].map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
        </div>
        <div className="row">
          <div className="field"><label>Amount</label><input type="number" step="0.01" required value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></div>
          <div className="field"><label>Currency</label><input maxLength={3} value={form.currencyCode} onChange={e => setForm(f => ({ ...f, currencyCode: e.target.value.toUpperCase() }))} /></div>
          <div className="field"><label>Reference</label><input value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} /></div>
        </div>
        {clientId && (
          <>
            <h3>Allocate to open invoices</h3>
            {open.length === 0 && <div className="muted">No open invoices for this client.</div>}
            <table className="table">
              <thead><tr><th>Number</th><th>Due</th><th style={{ textAlign: 'right' }}>Balance</th><th style={{ textAlign: 'right' }}>Allocate</th></tr></thead>
              <tbody>
                {open.map(d => {
                  const bal = Number(d.total) - Number(d.paidTotal);
                  const alloc = allocs.find(a => a.documentId === d.id);
                  return (
                    <tr key={d.id}>
                      <td>{d.fullNumber}</td>
                      <td>{date(d.dueDate)}</td>
                      <td style={{ textAlign: 'right' }}>{money(bal, d.currencyCode)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <input type="number" step="0.01" style={{ width: 100 }} value={alloc?.amount ?? ''} onChange={e => {
                          setAllocs(a => {
                            const others = a.filter(x => x.documentId !== d.id);
                            return e.target.value ? [...others, { documentId: d.id, amount: e.target.value }] : others;
                          });
                        }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="muted" style={{ textAlign: 'right' }}>Allocated {allocSum.toFixed(2)} / {Number(form.amount).toFixed(2)}</div>
          </>
        )}
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy || !canSubmit}>{busy ? <span className="spinner" /> : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
}
